import React, { useState, useCallback, useEffect } from 'react';
import { prop } from 'ramda';
import Form from '@core/js/components/common/Form';
import { useTranslations } from '@core/js/hooks/i18n';
import { Loader } from '@core/js/components/common/Loader';
import { Button } from '@core/js/components/common/Button';
import { useIsMounted } from '@core/js/hooks/useIsMounted';
import { ReCaptcha } from '@core/js/components/main/ReCaptcha';
import { Input } from '@theme/js/components/common/Fields/Input';
import { useUserSelector } from '@core/js/store/hooks';
import { ErrorMessage } from '@core/js/components/common/ErrorMessage';
import { required, length } from '@core/js/utils/Validation/validationRules';
import { WarningMessage } from '@core/js/components/common/Fields/WarningMessage';

import {
    useGetTitleAsync,
    useValidateTwoFactorToken,
    useResendCode,
    useRecaptchaErrorHandler
} from '@core/js/components/popups/TwoFactorAuthenticationPopup/components/hooks';

import style from './TwoFactorAuthentication.scss';

const validationConfig = {
    token: [
        required({ error: 'playtech.mobile.system.2FA.popup.token.tooltip.required' }),
        length({ min: 6, max: 6, error: 'playtech.mobile.system.2FA.popup.token.tooltip.length' })
    ]
};

const useGetTitle = () => {
    const t = useTranslations('playtech.mobile.system.2FA.popup.title.');

    return useCallback((templateId) => {
        switch (templateId) {
            case 'two_factor_email': return t('email');
            case 'two_factor_sms': return t('SMS');
            default: return t('default');
        }
    }, [t]);
};

export const TwoFactorAuthentication = ({ title, templateId, onSuccess, resendHandler, onClose, onCancel }) => {
    const [error, setError] = useState();
    const [isResendDisabled, setIsResendDisabled] = useState();
    const [displayTitle, setTitle] = useState();
    const isMounted = useIsMounted();
    const [isRecaptchaVisible, setIsRecaptchaVisible] = useState(false);
    const showRecaptcha = useCallback(() => setIsRecaptchaVisible(true), [setIsRecaptchaVisible]);
    const hideRecaptcha = useCallback(() => setIsRecaptchaVisible(false), [setIsRecaptchaVisible]);

    const getTitle = useGetTitle();
    const getTitleAsync = useGetTitleAsync();
    const validateTwoFactorToken = useValidateTwoFactorToken({ onSuccess, onClose, setError });
    const resendCode = useResendCode({
        setError,
        isMounted,
        resendHandler,
        showRecaptcha,
        isResendDisabled,
        isRecaptchaVisible,
        setIsResendDisabled
    });
    const recaptchaErrorHandler = useRecaptchaErrorHandler({ setError, hideRecaptcha });

    const t = useTranslations('playtech.mobile.system.2FA.popup.');
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    useEffect(() => {
        if (displayTitle || !isMounted()) {
            return;
        }

        if (title) {
            setTitle(title);
        } else if (templateId) {
            setTitle(getTitle(templateId));
        } else if (isLoggedIn && isMounted()) {
            (async () => {
                const ttl = await getTitleAsync();

                isMounted() && setTitle(ttl);
            })();
        } else {
            setTitle(t('title.default'));
        }
    }, [title, displayTitle, isLoggedIn, t, setTitle, getTitle, isMounted, getTitleAsync, templateId]);

    useEffect(() => {
        isRecaptchaVisible
            ? setError(t('playtech.mobile.system.recaptcha.error.captchaNotPassed'))
            : setError();
    }, [isRecaptchaVisible, setError, t]);

    return <Form
        onSubmit={validateTwoFactorToken}
        validationConfig={validationConfig}
        render={({ submitting, handleSubmit }) => (
            <div>
                <form
                    onSubmit={event => event.preventDefault()} /* TotalCasino changes */
                >
                    {isResendDisabled && (
                        <WarningMessage
                            warning={t('resendCoolDownError')}
                        />
                    )}
                    {error && (
                        <ErrorMessage
                            error={error}
                            animate={true}
                            className={style.error}
                        />
                    )}
                    {displayTitle
                        ? (<span className={style.description}>{displayTitle}</span>)
                        : (<Loader />)
                    }

                    <Input
                        type="text"
                        name="token"
                        className={style.control}
                        classNames={style} /* TotalCasino changes */
                        autoComplete="one-time-code"
                        placeholder={t('token.placeholder')}
                        aria-label={t(['token.placeholder', 'playtech.mobile.system.input.aria-label'])}
                    />
                    {isRecaptchaVisible && (
                        <div className={style.recaptcha}>
                            <ReCaptcha
                                operation="login"
                                onValidate={hideRecaptcha}
                                onError={recaptchaErrorHandler}
                            />
                        </div>
                    )}
                    <span className={style.resendLink}>
                        {t('didNotReceive')} <a href="#" className={`${isResendDisabled || isRecaptchaVisible ? style.isResendDisabled : ''}`} onClick={resendCode}>{t('resend')}</a>
                    </span>
                    <div className={style.formActions}>
                        <Button
                            type="button"
                            color="negative"
                            onClick={onCancel}
                            className={style.btn}
                        >
                            {t('playtech.mobile.system.cancel.button.title')}
                        </Button>
                        <Button
                            type="button" /* TotalCasino changes */
                            className={style.btn}
                            onClick={handleSubmit} /* TotalCasino changes */
                            disabled={submitting || isRecaptchaVisible}
                        >
                            {t('button.verify')}
                        </Button>
                    </div>
                </form>
            </div>
        )}
    />;
};
